// apps/admin/src/api/queries/credit.queries.ts
//
// Phase 3.4b Wave 7 Task 15 — TanStack Query hooks wrapping the admin
// credit API client (`../credit.api.ts`). Centralizes query keys + cache
// invalidation so the AdminCreditsListPage (T17), MerchantCreditTab (T16),
// CreditEditorDialog (this Wave) and CreditRevokeDialog (sibling T16) all
// stay in lock-step on cache shape.
//
// Per CONTEXT.md
//   • credit lifecycle drives invalidation:
//                grant/edit → invalidate ['admin','credits'] (cross-merchant
//                list) + ['admin','merchant',merchantId,'credit'] (per-merchant tab)
//                revoke    → same set; FE stays consistent.
//   • mutation idempotency lives at the wire layer (credit.api)
//                so hooks just forward args.
//
// Cache key convention:
//   ['admin', 'credits', filters]            — cross-merchant list
//   ['admin', 'merchant', merchantId, 'credit', 'history', filters]
//                                             — per-merchant infinite history

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  CreateCreditInput,
  UpdateCreditInput,
  CreditPayload,
} from '@printway/shared';

import {
  grantCredit,
  editCredit,
  revokeCredit,
  getMerchantCreditHistory,
  listAdminCredits,
  type MerchantCreditHistoryFilters,
  type MerchantCreditHistoryResponse,
  type AdminCreditsListFilters,
  type AdminCreditsListResponse,
} from '../credit.api.js';

// ---------------------------------------------------------------------------
// Shared cache invalidation
// ---------------------------------------------------------------------------

/**
 * Invalidate every cache slice that could change after a credit
 * grant/edit/revoke for `merchantId`. Centralized here so all three
 * mutation hooks stay in sync — adding a new credit-derived view later
 * means extending this one helper, not three call sites.
 */
function useInvalidateCreditCaches(): (merchantId: string) => void {
  const qc = useQueryClient();
  return (merchantId: string): void => {
    void qc.invalidateQueries({ queryKey: ['admin', 'credits'] });
    void qc.invalidateQueries({
      queryKey: ['admin', 'merchant', merchantId, 'credit'],
    });
    // Merchant detail (Overview tab credit blocks) + list rows read credit
    // totals from separate cache slices — invalidate them too so a grant/edit/
    // revoke reflects immediately instead of showing stale numbers (debug
    // 2026-07-14). Keys mirror MerchantDetailPage useQuery + MerchantsListPage.
    void qc.invalidateQueries({
      queryKey: ['admin', 'merchants', merchantId, 'detail'],
    });
    void qc.invalidateQueries({ queryKey: ['admin', 'merchant', merchantId] });
    void qc.invalidateQueries({ queryKey: ['admin', 'merchants', 'list'] });
  };
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

interface GrantCreditVars {
  merchantId: string;
  dto: CreateCreditInput;
}

/** Grant a new credit to a merchant. Auto-invalidates list + per-merchant caches. */
export function useGrantCredit() {
  const invalidate = useInvalidateCreditCaches();
  return useMutation<CreditPayload, Error, GrantCreditVars>({
    mutationFn: ({ merchantId, dto }) => grantCredit(merchantId, dto),
    onSuccess: (_data, { merchantId }) => {
      invalidate(merchantId);
    },
  });
}

interface EditCreditVars {
  merchantId: string;
  creditId: string;
  dto: UpdateCreditInput;
}

/** Edit (limit-only-increase / period / options) an existing credit. */
export function useEditCredit() {
  const invalidate = useInvalidateCreditCaches();
  return useMutation<CreditPayload, Error, EditCreditVars>({
    mutationFn: ({ merchantId, creditId, dto }) =>
      editCredit(merchantId, creditId, dto),
    onSuccess: (_data, { merchantId }) => {
      invalidate(merchantId);
    },
  });
}

interface RevokeCreditVars {
  merchantId: string;
  creditId: string;
  reason: string;
}

/** Revoke an active credit. Reason is required (10-500 chars). */
export function useRevokeCredit() {
  const invalidate = useInvalidateCreditCaches();
  return useMutation<CreditPayload, Error, RevokeCreditVars>({
    mutationFn: ({ merchantId, creditId, reason }) =>
      revokeCredit(merchantId, creditId, reason),
    onSuccess: (_data, { merchantId }) => {
      invalidate(merchantId);
    },
  });
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Per-merchant credit history — infinite (cursor) paginated. Pass the
 * non-pagination filters in `filters`; cursor is managed internally by
 * useInfiniteQuery.
 *
 * Cache slice: `['admin','merchant',merchantId,'credit','history',filters]`
 * — invalidating any of `['admin','merchant',merchantId,'credit']` or
 * narrower scopes will refetch this query.
 */
export function useMerchantCreditHistory(
  merchantId: string,
  filters: Omit<MerchantCreditHistoryFilters, 'cursor'> = {},
) {
  return useInfiniteQuery<
    MerchantCreditHistoryResponse,
    Error,
    MerchantCreditHistoryResponse,
    readonly unknown[],
    string | undefined
  >({
    queryKey: [
      'admin',
      'merchant',
      merchantId,
      'credit',
      'history',
      filters,
    ] as const,
    initialPageParam: undefined,
    queryFn: ({ pageParam }) =>
      getMerchantCreditHistory(merchantId, {
        ...filters,
        cursor: pageParam,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(merchantId),
    staleTime: 30_000,
  });
}

/**
 * Cross-merchant credit list — offset paginated. Pass the full filter set
 * (page + pageSize live inside `filters`) so changing the page produces
 * a distinct cache slice, matching the URL-driven query state pattern
 * used by `AdminUsersPage`.
 */
export function useAdminCreditsList(filters: AdminCreditsListFilters = {}) {
  return useQuery<AdminCreditsListResponse, Error>({
    queryKey: ['admin', 'credits', filters] as const,
    queryFn: () => listAdminCredits(filters),
    staleTime: 30_000,
  });
}
